import { ElementNode, SerializedElementNode, EditorConfig, LexicalNode } from 'lexical';
import { addClassNamesToElement } from '@lexical/utils';

export interface NonNumericFact {
  tag: 'ix:nonNumeric';
  name: string;
  format?: string;
  factId: string;
  contextRef: string;
  utrvId: string;
  valueListCode?: string;
}

export interface NonFractionFact {
  tag: 'ix:nonFraction';
  name: string;
  format?: string;
  factId: string;
  unitRef: string;
  contextRef: string;
  decimals?: number;
  scale?: number | string;
  utrvId: string;
  valueListCode?: string;
}

export type IXBRLFact = NonNumericFact | NonFractionFact;
export type IXBRLNodeProps = IXBRLFact & { elementType?: string };

export const isNonFractionFact = (xbrl: IXBRLNodeProps): xbrl is NonFractionFact => {
  return xbrl.tag === 'ix:nonFraction';
};

export interface IXBRLElementNodeSerialized extends SerializedElementNode {
  xbrl: IXBRLNodeProps;
}

export class IXBRLNode extends ElementNode {
  private readonly xbrl: IXBRLNodeProps;
  private elementType: string;

  constructor(props: IXBRLNodeProps, key?: string) {
    super(key);
    this.xbrl = props;
    this.elementType = props?.elementType ?? 'span';
  }

  static getType(): string {
    return 'ixbrl-element';
  }

  static clone(node: IXBRLNode): IXBRLNode {
    return new IXBRLNode(node.xbrl, node.__key);
  }

  createDOM(config: EditorConfig): HTMLElement {
    const element = document.createElement(this.elementType);
    addClassNamesToElement(element, 'ixbrl-element', config.theme.ixbrlNode);
    element.setAttribute('xbrlTag', this.xbrl.tag);
    element.setAttribute('contextRef', this.xbrl.contextRef);
    element.setAttribute('factId', this.xbrl.factId);
    element.setAttribute('title', this.xbrl.name);
    element.setAttribute('xbrlName', this.xbrl.name);

    if (this.xbrl.tag === 'ix:nonFraction') {
      element.setAttribute('unitRef', this.xbrl.unitRef);
      if (this.xbrl.decimals) {
        element.setAttribute('decimals', this.xbrl.decimals.toString());
      }
      if (this.xbrl.scale) {
        element.setAttribute('scale', this.xbrl.scale.toString());
      }
    }
    return element;
  }

  updateDOM(): boolean {
    return false;
  }

  static importJSON(serializedNode: IXBRLElementNodeSerialized): IXBRLNode {
    const xbrlProps = serializedNode.xbrl;
    if (xbrlProps.tag === 'ix:nonFraction') {
      return new IXBRLNode({
        tag: xbrlProps.tag,
        name: xbrlProps.name,
        unitRef: xbrlProps.unitRef,
        contextRef: xbrlProps.contextRef,
        factId: xbrlProps.factId,
        decimals: xbrlProps.decimals,
        scale: xbrlProps.scale,
        utrvId: xbrlProps.utrvId,
        valueListCode: xbrlProps.valueListCode,
      });
    } else {
      return new IXBRLNode({
        tag: xbrlProps.tag,
        name: xbrlProps.name,
        contextRef: xbrlProps.contextRef,
        factId: xbrlProps.factId,
        utrvId: xbrlProps.utrvId,
        valueListCode: xbrlProps.valueListCode,
      });
    }
  }

  exportJSON(): IXBRLElementNodeSerialized {
    return {
      ...super.exportJSON(),
      xbrl: this.xbrl,
      type: this.getType(),
    };
  }

  getTagName(): string {
    return this.xbrl.tag;
  }

  getName(): string {
    return this.xbrl.name;
  }

  getXbrl() {
    return this.xbrl;
  }

  // Override to prevent text insertion at the end of IXBRL node
  canInsertTextAfter(): boolean {
    return false;
  }

  // Override to prevent text insertion at the beginning of IXBRL node
  canInsertTextBefore(): boolean {
    return false;
  }

  public canBeEmpty(): boolean {
    return false;
  }

  public isInline(): boolean {
    return true;
  }
}

export function $createIxbrlNode(props: IXBRLNodeProps): IXBRLNode {
  return new IXBRLNode(props);
}

export function $isXbrlNode(node: LexicalNode | null | undefined): node is IXBRLNode  {
  return node instanceof IXBRLNode;
}
